import { Link, useLocation } from '@tanstack/react-router';

import { Skeleton } from '@/shared/components/shadcn/skeleton';
import { useSavedQueries } from '@/shared/hooks/use-saved-queries';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { cn } from '@/shared/utils/cn';
import { InsightFilterHeader } from './insight-filter.header';

const getTabsWithWorkspaceId = (workspaceId: string) => [
  { value: 'overview', label: 'Overview', path: `/workspace/${workspaceId}/insight/overview` },
  {
    value: 'performance',
    label: 'Performance',
    path: `/workspace/${workspaceId}/insight/performance`,
  },
  {
    value: 'conversation',
    label: 'Conversation',
    path: `/workspace/${workspaceId}/insight/conversation`,
  },
  {
    value: 'people',
    label: 'People',
    path: `/workspace/${workspaceId}/insight/people`,
  },
  { value: 'posts', label: 'Posts', path: `/workspace/${workspaceId}/insight/posts` },
];

export function InsightHeader() {
  const location = useLocation();
  const { workspaceId } = useWorkspaceId();
  const { data: savedQueries } = useSavedQueries({ workspaceId });
  const { query: selectedQuery } = useSelectedSavedQuery();

  if (savedQueries == null) {
    return <InsightHeaderSkeleton />;
  }

  if (selectedQuery == null) {
    return <InsightHeaderSkeleton />;
  }

  const tabs = getTabsWithWorkspaceId(workspaceId);

  return (
    <header className="bg-background sticky top-0 z-10">
      <InsightFilterHeader />

      {/* Tabs section */}
      <div className="px-6 py-3">
        <div className="bg-muted text-muted-foreground inline-flex w-full items-center justify-center rounded-lg p-1">
          <div className="grid w-full grid-cols-5">
            {tabs.map((tab) => (
              <Link
                key={tab.value}
                to={tab.path}
                search={location.search}
                className={cn(
                  "data-[active=true]:bg-background dark:data-[active=true]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[active=true]:border-input dark:data-[active=true]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[active=true]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                )}
                data-active={location.pathname === tab.path}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

function InsightHeaderSkeleton() {
  return (
    <header className="border-b border-zinc-200 px-6 py-3">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-[50px] w-32" />
          </div>
        </div>

        {/* Right side - Date range picker skeleton */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-[50px] w-64" />
        </div>
      </div>
    </header>
  );
}
